import styles from './MentionComposerInput.module.scss';

const UNRESOLVED_ATTR = 'data-mention-unresolved';
const TRIGGER_ATTR = 'data-mention-trigger';
const QUERY_ATTR = 'data-mention-query';
const TEXT_ATTR = 'data-mention-text';

const UNRESOLVED_TOKEN_RE = /(?:^|[\s\u00a0])([@#][^\s\u00a0]+)(?=[\s\u00a0])/g;

export const isUnresolvedMentionElement = (
  node: Node | null | undefined
): node is HTMLElement =>
  node instanceof HTMLElement && node.getAttribute(UNRESOLVED_ATTR) === '1';

const createQuestionBadge = (): HTMLElement => {
  const badge = document.createElement('span');
  badge.className = styles.unresolvedBadge;
  badge.setAttribute('aria-hidden', 'true');
  return badge;
};

export const createUnresolvedMentionElement = (
  trigger: '@' | '#',
  query: string
): HTMLElement => {
  const text = `${trigger}${query}`;
  const el = document.createElement('span');
  el.setAttribute(UNRESOLVED_ATTR, '1');
  el.setAttribute(TRIGGER_ATTR, trigger);
  el.setAttribute(QUERY_ATTR, query);
  el.setAttribute(TEXT_ATTR, text);
  el.setAttribute('contenteditable', 'false');
  el.setAttribute('role', 'note');
  el.setAttribute('aria-label', `Unresolved mention: ${text}`);
  el.className = styles.unresolvedMention;

  const label = document.createElement('span');
  label.textContent = text;
  el.appendChild(label);
  el.appendChild(createQuestionBadge());
  return el;
};

export const readUnresolvedMentionText = (el: HTMLElement): string =>
  el.getAttribute(TEXT_ATTR) ?? el.textContent ?? '';

interface UnresolvedMatch {
  start: number;
  end: number;
  trigger: '@' | '#';
  query: string;
}

const findUnresolvedMatches = (value: string): UnresolvedMatch[] => {
  const matches: UnresolvedMatch[] = [];
  UNRESOLVED_TOKEN_RE.lastIndex = 0;
  let match = UNRESOLVED_TOKEN_RE.exec(value);
  while (match) {
    const token = match[1];
    const trigger = token[0];
    if (trigger === '@' || trigger === '#') {
      const tokenStart = match.index + match[0].length - token.length;
      matches.push({
        start: tokenStart,
        end: tokenStart + token.length,
        trigger,
        query: token.slice(1),
      });
    }
    match = UNRESOLVED_TOKEN_RE.exec(value);
  }
  return matches;
};

const wrapUnresolvedInTextNode = (textNode: Text): void => {
  const value = textNode.nodeValue ?? '';
  const matches = findUnresolvedMatches(value);
  if (matches.length === 0) {
    return;
  }

  let current: Text = textNode;
  for (let i = matches.length - 1; i >= 0; i--) {
    const { start, end, trigger, query } = matches[i];
    const parent = current.parentNode;
    if (!parent) {
      return;
    }

    current.splitText(end);
    const tokenNode = current.splitText(start);
    const el = createUnresolvedMentionElement(trigger, query);
    parent.replaceChild(el, tokenNode);
    if (current.nodeValue === '') {
      current.remove();
    }
  }
};

export const syncUnresolvedMentions = (root: HTMLElement): void => {
  const textNodes = Array.from(root.childNodes).filter(
    (node): node is Text => node.nodeType === Node.TEXT_NODE
  );
  textNodes.forEach(wrapUnresolvedInTextNode);
};

export interface UnresolvedTokenTarget {
  startNode: Text;
  startOffset: number;
  trigger: '@' | '#';
  query: string;
}

export const wrapUnresolvedToken = ({
  startNode,
  startOffset,
  trigger,
  query,
}: UnresolvedTokenTarget): void => {
  if (!startNode.isConnected) {
    return;
  }
  const parent = startNode.parentNode;
  if (!parent) {
    return;
  }

  const expected = `${trigger}${query}`;
  const value = startNode.nodeValue ?? '';
  if (value.slice(startOffset, startOffset + expected.length) !== expected) {
    return;
  }

  startNode.splitText(startOffset + expected.length);
  const tokenNode = startNode.splitText(startOffset);
  const el = createUnresolvedMentionElement(trigger, query);
  parent.replaceChild(el, tokenNode);
  if (startNode.nodeValue === '') {
    startNode.remove();
  }
};

export const findUnresolvedMentionFromTarget = (
  root: HTMLElement,
  target: EventTarget | null
): HTMLElement | null => {
  if (!(target instanceof Node)) {
    return null;
  }
  const el =
    target instanceof Element
      ? target.closest(`[${UNRESOLVED_ATTR}]`)
      : target.parentElement?.closest(`[${UNRESOLVED_ATTR}]`);
  if (!(el instanceof HTMLElement) || !root.contains(el)) {
    return null;
  }
  return el;
};

export const unresolvedElementToTrigger = (
  el: HTMLElement
): {
  trigger: '@' | '#';
  query: string;
  unresolvedElement: HTMLElement;
} | null => {
  const triggerChar = el.getAttribute(TRIGGER_ATTR);
  if (triggerChar !== '@' && triggerChar !== '#') {
    return null;
  }
  return {
    trigger: triggerChar,
    query: el.getAttribute(QUERY_ATTR) ?? '',
    unresolvedElement: el,
  };
};
